// DAS-2532: shared helpers for the per-alert overall urgency score.
// The numeric urgency_score (0-100) and its exact-calculation urgency_score_explanation
// are computed + stored server-side (pythonscripts/alert_urgency_core.py). The frontend
// only turns the number into a band + colour for the pill / accent bar.
//
// NOTE: keep these thresholds in sync with alert_urgency_core.py
// (BAND_HIGH_MIN / BAND_MODERATE_MIN). They are PROVISIONAL — refit to the live
// urgency distribution once the score has been computed across the population.
export const URGENCY_HIGH_MIN = 60;
export const URGENCY_MODERATE_MIN = 45;

// Numeric score from an alert object, or null if not yet computed.
export function urgencyValue(alert) {
  const v = alert?.urgency_score;
  if (v === null || v === undefined || v === '') return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

// 'HIGH' | 'MODERATE' | 'LOW' | null
// Prefers the server-computed urgency_band (written with the configured thresholds);
// falls back to deriving from the score with the default cutoffs for rows scored
// before urgency_band existed.
export function urgencyBand(scoreOrAlert) {
  if (scoreOrAlert && typeof scoreOrAlert === 'object') {
    const stored = scoreOrAlert.urgency_band;
    if (stored === 'HIGH' || stored === 'MODERATE' || stored === 'LOW') return stored;
  }
  const score = typeof scoreOrAlert === 'object' ? urgencyValue(scoreOrAlert) : scoreOrAlert;
  if (score === null || score === undefined || !Number.isFinite(score)) return null;
  if (score >= URGENCY_HIGH_MIN) return 'HIGH';
  if (score >= URGENCY_MODERATE_MIN) return 'MODERATE';
  return 'LOW';
}

// MUI colour name for the urgency band — deliberately NOT the green=good palette used
// by confidence/opportunity (HIGH urgency must grab attention without reading as "bad").
export function urgencyColor(band) {
  switch (band) {
    case 'HIGH': return 'error';
    case 'MODERATE': return 'warning';
    case 'LOW': return 'info';
    default: return 'default';
  }
}

// Hex accent for the 4px left-bar (maps to the same palette).
export function urgencyAccent(band) {
  switch (band) {
    case 'HIGH': return '#d32f2f';      // error.main
    case 'MODERATE': return '#ed6c02';  // warning.main
    case 'LOW': return '#0288d1';       // info.main
    default: return 'transparent';
  }
}

// Short emoji + band label for the compact pill, e.g. "🔴 HIGH".
export function urgencyPillLabel(band) {
  switch (band) {
    case 'HIGH': return '🔴 HIGH';
    case 'MODERATE': return '🟠 MOD';
    case 'LOW': return '🟢 LOW';
    default: return '';
  }
}

// Sort comparator (high → low). Alerts with no urgency_score sort to the bottom,
// then tie-break by final confidence, then ticker.
export function compareByUrgency(a, b) {
  const ua = urgencyValue(a);
  const ub = urgencyValue(b);
  const va = ua === null ? -1 : ua;
  const vb = ub === null ? -1 : ub;
  if (va !== vb) return vb - va;
  const ca = parseFloat(a?.pf_final_confidence_score) || 0;
  const cb = parseFloat(b?.pf_final_confidence_score) || 0;
  if (ca !== cb) return cb - ca;
  return (a?.ticker || '').localeCompare(b?.ticker || '');
}
